/**
 * 互動影片問答的「停點」模型（VideoQuest.console.steps.md 的 VF2–VF5）。
 *
 * ⚠⚠ **停點不是新發明的概念，是學生端既有行為的名字。**
 * `DiaryVideo.vue:689-694` 答完一題後的分支是：
 *
 *     if (上一題.time == 下一題.time) playQuestAudio();   // 不恢復影片，接著問
 *     else if (上一題.time != -1)     playMainVideo();    // 恢復播放
 *
 * 也就是**同一個 `time` 的小題會連續問完才放行影片**。所以「停點」＝
 * `quests[]` 裡 `time` 相同的那一組。stage 上有 42 題的小題時間重複，
 * 那些本來就是多題停點。
 *
 * ⚠ 分組是 `time` **完全相等**，不是接近。30 秒與 31 秒是兩個停點——
 * 這也是為什麼需要「相距太近」的提醒。
 */

/** `-1` = 影片播完後。排序時視為無限大，永遠在最後。 */
export const TIME_END = -1;

/** 一個停點最多幾題。 */
export const MAX_QUEST_PER_STOP = 5;

/** 兩個標記的像素距離小於這個值就合併成群組。 */
export const MERGE_PX = 22;

/** 相距小於這個秒數的兩個停點要提醒。 */
export const TOO_CLOSE_SECONDS = 3;

export function sortKey(time) {
    return time === TIME_END ? Number.MAX_SAFE_INTEGER : time;
}

/**
 * 把扁平的小題陣列分組成停點，依時間排序（`-1` 最後）。
 *
 * ⚠ 每個停點的 `quests` **保持原陣列裡的相對順序**——那就是學生的作答順序
 * （學生端照索引往下走）。排序必須 stable，否則老師調好的順序會在重新整理後跑掉。
 *
 * @returns `[{ time, quests: [...] }]`
 */
export function groupStops(quests) {
    const byTime = new Map();
    (quests || []).forEach((q) => {
        if (!q) {
            return;
        }
        const t = q.time;
        if (!byTime.has(t)) {
            byTime.set(t, []);
        }
        byTime.get(t).push(q);
    });
    return [...byTime.entries()]
        .map(([time, list]) => ({ time, quests: list }))
        .sort((a, b) => sortKey(a.time) - sortKey(b.time));
}

/**
 * 題號 → 跨停點連續編號。
 *
 * ⚠ 用 `Map` 以**小題物件**當 key，不是索引：停點重排之後索引會變，
 * 而編號要跟著題目本身。
 */
export function numberQuests(stops) {
    const numbers = new Map();
    let n = 0;
    stops.forEach((stop) => {
        stop.quests.forEach((q) => {
            n += 1;
            numbers.set(q, n);
        });
    });
    return numbers;
}

/**
 * 時間軸上的標記；太近的會合併成群組。
 *
 * ⚠⚠ 比較的是**與前一個標記的距離**，不是與群組第一個的距離。
 * 用群組第一個當基準的話，一串等距 20px 的標記會全部併成一個——
 * 而它們其實橫跨了整條軌道。
 *
 * ⚠ `-1`（播完後）不在時間軸上，呼叫端要先濾掉。
 *
 * @param stops 已排序的停點
 * @param trackWidth 軌道的像素寬度
 * @param duration 影片長度（秒）
 * @returns `[{ stops: [...], x }]`，`x` 是像素位置
 */
export function buildMarkers(stops, trackWidth, duration) {
    if (!trackWidth || !duration) {
        return [];
    }
    const out = [];
    let prevX = null;
    stops.forEach((stop) => {
        const x = (stop.time / duration) * trackWidth;
        if (prevX !== null && x - prevX < MERGE_PX) {
            out[out.length - 1].stops.push(stop);
        } else {
            out.push({ stops: [stop], x });
        }
        prevX = x;
    });
    return out;
}

/**
 * 每個停點與**最近的鄰居**相差幾秒；只有小於門檻的才回傳。
 *
 * ⚠ `-1` 不參與——它不在時間軸上，跟誰都不算「相距太近」。
 *
 * @returns `Map<time, 秒數>`
 */
export function tooCloseStops(stops) {
    const timed = stops.filter((s) => s.time !== TIME_END);
    const out = new Map();
    for (let i = 1; i < timed.length; i += 1) {
        const gap = timed[i].time - timed[i - 1].time;
        if (gap < TOO_CLOSE_SECONDS) {
            /// 兩邊都要標——老師看哪一個都該知道有問題
            const prev = out.get(timed[i - 1].time);
            const cur = out.get(timed[i].time);
            out.set(timed[i - 1].time, prev === undefined ? gap : Math.min(prev, gap));
            out.set(timed[i].time, cur === undefined ? gap : Math.min(cur, gap));
        }
    }
    return out;
}

/** 秒數 → `0:42`。⚠ 負數與非數字一律當 0，不要顯示 `NaN:NaN`。 */
export function clock(seconds) {
    const n = Math.max(0, Math.floor(Number(seconds) || 0));
    return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`;
}

/**
 * `mm:ss` 或純秒數 → 秒。看不懂就回 `null`（呼叫端維持原值）。
 *
 * ⚠ 刻意接受純數字：老師打「90」比打「1:30」快，兩種都該通。
 */
export function parseClock(text, maxSeconds) {
    const s = String(text == null ? '' : text).trim();
    if (!s) {
        return null;
    }
    let seconds = null;
    const mmss = s.match(/^(\d+):([0-5]?\d)$/);
    if (mmss) {
        seconds = Number(mmss[1]) * 60 + Number(mmss[2]);
    } else if (/^\d+$/.test(s)) {
        seconds = Number(s);
    }
    if (seconds === null || !Number.isFinite(seconds)) {
        return null;
    }
    /// ⚠ 夾在影片長度內：超過片長的停點學生永遠遇不到
    const max = Number.isFinite(maxSeconds) && maxSeconds > 0
        ? Math.floor(maxSeconds) : null;
    if (max !== null && seconds > max) {
        seconds = max;
    }
    return Math.max(0, seconds);
}

/* ─────────────────────────────────────────────────────────────────────
 * VF8 音檔狀態（需求方 2026-09-17 規格、2026-09-21 補充）
 * ───────────────────────────────────────────────────────────────────── */

/**
 * 一句語音的狀態。**以「句」為單位**（規格第 14 點），一句一個鍵、不共用。
 *
 * ⚠⚠ `STALE`（待更新）**只有前端知道**。後端要等存檔之後、比對 `tts_text_hash`
 * 才看得出文字變了，所以它不會從 API 回來——是編輯器自己比對「現在輸入框裡的值」
 * 與「載入時的值」算出來的。**重新整理頁面它就會消失**，因為沒存檔的修改本來就不見了。
 *
 * ⚠ `MISSING` 不在需求方列的四種狀態裡，但真的存在：老師打了字、還沒按過儲存，
 * 這時有文字卻連一個舊檔都沒有。併進 `STALE` 會讓播放鍵可以按卻播不出東西，
 * 所以獨立成一個狀態——外觀跟 `STALE` 一樣（▶ ＋驚嘆號角標），但**不可點**。
 */
export const AUDIO = {
    /** 規格 7：可播放。實心 ▶。 */
    READY: 'ready',
    /** 規格 8：產生中。◌ 並停用。⚠ 後端的 `pending` 與 `processing` 都算。 */
    BUSY: 'busy',
    /** 規格 9：文字已改、音檔還是舊的。▶ ＋驚嘆號角標，**仍可播舊檔**。 */
    STALE: 'stale',
    /** 規格 10：產生失敗。↻，點一下重試。 */
    FAILED: 'failed',
    /** 有文字但從來沒產生過（老師還沒存過檔）。外觀同 STALE，但沒有舊檔可播。 */
    MISSING: 'missing',
    /** 規格 11：沒有文字 → 不顯示播放鍵。 */
    NONE: 'none',
};

/** 這些狀態有東西可以播。⚠ `STALE` 播的是**舊檔**，這是規格第 9 點要的。 */
const PLAYABLE = [AUDIO.READY, AUDIO.STALE];

/** 規格 17：依序試聽只排入這些。 */
export function isQueueable(state) {
    return PLAYABLE.indexOf(state) !== -1;
}

export function isPlayable(state) {
    return PLAYABLE.indexOf(state) !== -1;
}

/**
 * 算一句的狀態。
 *
 * @param text     這一句現在的文字（輸入框裡的值）
 * @param baseline 載入／上次存檔時的文字。`undefined` = 沒有基準（當作沒改過）
 * @param media    `mediaMap[audio_media_id]`，沒有就是 null／undefined
 *
 * ⚠ **判斷順序不能換**：
 * 1. 沒文字 → `NONE`（規格 11 優先於一切，沒文字就不該有鍵）
 * 2. 產生中 → `BUSY`（比 `STALE` 優先：正在產生的東西，再標「待更新」只會讓人以為壞了）
 * 3. 失敗 → `FAILED`（比 `STALE` 優先，同上）
 * 4. 文字改過 → `STALE` / `MISSING`
 * 5. 其餘看後端狀態
 */
export function audioState(text, baseline, media) {
    const now = (text || '').trim();
    if (!now) {
        return AUDIO.NONE;
    }
    const status = media && media.process_status;
    if (status === 'pending' || status === 'processing') {
        return AUDIO.BUSY;
    }
    if (status === 'failed') {
        return AUDIO.FAILED;
    }
    /// ⚠ `baseline === undefined` 是「沒有基準」不是「基準是空字串」，
    /// 兩者要分開：前者不算改過，後者算。
    const changed = baseline !== undefined && now !== (baseline || '').trim();
    if (changed) {
        return media && media.url ? AUDIO.STALE : AUDIO.MISSING;
    }
    if (media && media.url && status === 'ready') {
        return AUDIO.READY;
    }
    return AUDIO.MISSING;
}

/**
 * 規格 15 的播放順序：題目 → 選項1 → 選項1回饋 → 選項2 → …
 *
 * ⚠⚠ **回饋要用選項的順序去查 `answers`，不能直接 `Object.values(answers)`。**
 * `answers` 的 key 是 `option.id` 而不是索引（結構問題 3），物件的列舉順序
 * 與選項在畫面上的順序沒有關係——直接列舉會讓試聽的順序跟學生聽到的不一樣。
 */
export function playOrder(quest) {
    const out = [{ kind: 'title', opt: null }];
    for (const opt of (quest.options || [])) {
        out.push({ kind: 'option', opt });
        const answer = (quest.answers || {})[opt.id];
        if (answer) {
            out.push({ kind: 'response', opt });
        }
    }
    return out;
}

/** 一句的文字。`kind` 是 `playOrder` 給的那三種。 */
export function slotText(quest, kind, opt) {
    if (kind === 'title') {
        return (quest.quest_title && quest.quest_title.title) || '';
    }
    if (kind === 'option') {
        return (opt && opt.title) || '';
    }
    const answer = (quest.answers || {})[opt && opt.id];
    return (answer && answer.response_text) || '';
}

/** 一句的音檔 `media_id`。⚠ 三個位置的欄位名不同，別寫死成同一個。 */
export function slotAudioId(quest, kind, opt) {
    if (kind === 'title') {
        return (quest.quest_title && quest.quest_title.audio_media_id) || null;
    }
    if (kind === 'option') {
        return (opt && opt.audio_media_id) || null;
    }
    const answer = (quest.answers || {})[opt && opt.id];
    return (answer && answer.response_audio_media_id) || null;
}

/**
 * 一句的識別鍵。用在 dirty 基準表與「現在在播哪一句」。
 *
 * ⚠ 用 `option.id` 而不是索引：選項可以拖曳排序，用索引的話一調順序，
 * 基準表就整個對錯人——症狀是「動了選項順序，所有選項都變成待更新」。
 */
export function slotKey(questNo, kind, opt) {
    if (kind === 'title') {
        return `q${questNo}:title`;
    }
    return `q${questNo}:${kind}:${(opt && opt.id) != null ? opt.id : '?'}`;
}

/**
 * 規格 19 的小標：依**嚴重度**取代顯示，不是全部列出來。
 * 失敗 ＞ 產生中 ＞ 待更新 ＞ 全部就緒。
 *
 * @returns `{ level, done, total }`；`level` 是 `failed`／`busy`／`stale`／`ready`，
 *          沒有任何一句有文字時回 `null`（不顯示小標）。
 */
export function audioSummary(states) {
    const total = states.filter((s) => s !== AUDIO.NONE).length;
    if (!total) {
        return null;
    }
    const count = (s) => states.filter((x) => x === s).length;
    const failed = count(AUDIO.FAILED);
    if (failed) {
        return { level: 'failed', done: failed, total };
    }
    const busy = count(AUDIO.BUSY);
    if (busy) {
        return { level: 'busy', done: total - busy, total };
    }
    const stale = count(AUDIO.STALE) + count(AUDIO.MISSING);
    if (stale) {
        return { level: 'stale', done: stale, total };
    }
    return { level: 'ready', done: total, total };
}


/** Build one answer for saving without changing the editor's selection state. */
export function videoAnswerForSave(sub, opt) {
    const answers = sub.answers || {};
    const hasCorrect = (sub.options || []).some((option) => {
        const answer = answers[String(option.id)];
        return answer && answer.is_correct === true;
    });
    return Object.assign({}, answers[String(opt.id)] || {}, {
        is_correct: !hasCorrect || !!(answers[String(opt.id)] || {}).is_correct,
    });
}

/**
 * Build the `video_quest` save payload from the editor's tree.
 *
 * The transport format hangs each answer under its option (`option.answer`),
 * unlike the stored `answers` map; see `UnitQuestEdit.vue` for why.
 */
export function videoQuestForSave(videoQuest) {
    return {
        ans_type: videoQuest.ans_type,
        quests: (videoQuest.quests || []).map((sub) => ({
            time: sub.time,
            type: sub.type,
            // Legacy questions only have URLs, and the backend does not carry them over.
            cover_img: sub.cover_img,
            cover_media_id: sub.cover_media_id,
            quest_title: {
                title: (sub.quest_title || {}).title || '',
                audio_link: (sub.quest_title || {}).audio_link,
                audio_media_id: (sub.quest_title || {}).audio_media_id,
            },
            options: (sub.options || []).map((opt) => ({
                id: opt.id,
                title: opt.title,
                img_url: opt.img_url,
                img_media_id: opt.img_media_id,
                audio_link: opt.audio_link,
                audio_media_id: opt.audio_media_id,
                code: opt.code,
                appear_after_play_time: opt.appear_after_play_time,
                // No checked answers means every current option is correct on save.
                answer: videoAnswerForSave(sub, opt),
            })),
        })),
    };
}
